import type { MemberSearchData, WorkArtistData } from '../dto/EntityRepository';
import type { UpdateWorkArtistReq } from '../dto/ReqDtoRepository';
import { ArchiveRole, MemberType, WorkContentMode } from '../enum/EnumRepository';
import type { SettingData } from '../dto/EntityRepository';
import { isExposureLevelSearchable } from './exposureLevel';

export const EMPTY_WORK_ARTIST: WorkArtistData = {
  name: '',
  nameEn: '',
  linkedMemberAui: null,
  linkedMemberHandle: null,
};

export const normalizeWorkArtist = (
  artist: WorkArtistData | null | undefined,
): WorkArtistData | null => {
  if (!artist) return null;
  return {
    name: artist.name?.trim() ?? '',
    nameEn: artist.nameEn?.trim() ?? '',
    linkedMemberAui: artist.linkedMemberAui || null,
    linkedMemberHandle: artist.linkedMemberHandle || null,
  };
};

/** linkedMemberHandle은 응답 전용이므로 업데이트 요청에서 제외한다. name이 비면 null(Artist 전체 삭제). */
export const toUpdateWorkArtistReq = (
  artist: WorkArtistData | null | undefined,
): UpdateWorkArtistReq | null => {
  const normalized = normalizeWorkArtist(artist);
  if (!normalized?.name) return null;
  return {
    name: normalized.name,
    nameEn: normalized.nameEn,
    linkedMemberAui: normalized.linkedMemberAui,
  };
};

export const resolveWorkArtistName = (
  artist: WorkArtistData | null | undefined,
  mode: WorkContentMode = WorkContentMode.Orig,
  engVisible = true,
): string => {
  if (!artist) return '';
  if (mode === WorkContentMode.ENG && engVisible) {
    return artist.nameEn?.trim() || artist.name?.trim() || '';
  }
  return artist.name?.trim() || '';
};

export const isEligibleArtistMember = (member: MemberSearchData): boolean => {
  const isIndividual = member.memberType === MemberType.INDIVIDUAL;
  const isArtistArchive = member.archiveRole === ArchiveRole.ARTIST;
  const isSearchable = Boolean(
    member.exposureLevel && isExposureLevelSearchable(member.exposureLevel),
  );
  return isIndividual && isArtistArchive && isSearchable;
};

export const shouldShowWorkArtist = (
  setting: Pick<SettingData, 'memberType' | 'archiveRole'> | null | undefined,
): boolean => {
  if (!setting) return false;
  if (setting.memberType === MemberType.GROUP) return true;
  return setting.archiveRole != null && setting.archiveRole !== ArchiveRole.ARTIST;
};

/** GRID(work3) 전환은 Individual + Artist 아카이브만. */
export const isIndividualArtistArchive = (
  setting: Pick<SettingData, 'memberType' | 'archiveRole'> | null | undefined,
): boolean =>
  setting?.memberType === MemberType.INDIVIDUAL
  && setting.archiveRole === ArchiveRole.ARTIST;

export const isSameWorkArtist = (
  left: WorkArtistData | null | undefined,
  right: WorkArtistData | null | undefined,
): boolean => (
  (left?.name ?? '') === (right?.name ?? '')
  && (left?.nameEn ?? '') === (right?.nameEn ?? '')
  && (left?.linkedMemberAui ?? null) === (right?.linkedMemberAui ?? null)
  && (left?.linkedMemberHandle ?? null) === (right?.linkedMemberHandle ?? null)
);
